import { type SystemNotification, notificationSourceMeta, severityMeta } from '@/mocks/saGlobalNotificationCenter';

interface NotificationCardProps {
  notification: SystemNotification;
  isSelected: boolean;
  onToggleSelect: (id: number) => void;
  onView: (notification: SystemNotification) => void;
  onMarkRead: (notification: SystemNotification) => void;
  onToggleImportant: (notification: SystemNotification) => void;
  onArchive: (notification: SystemNotification) => void;
  onDelete: (notification: SystemNotification) => void;
}

function relativeTime(dateStr: string): string {
  if (!dateStr) return '--';
  try {
    const diff = Date.now() - new Date(dateStr).getTime();
    if (Number.isNaN(diff)) return '--';
    const mins = Math.floor(diff / 60000);
    if (mins < 1) return 'Ahora';
    if (mins < 60) return `Hace ${mins}m`;
    const hrs = Math.floor(mins / 60);
    if (hrs < 24) return `Hace ${hrs}h`;
    const days = Math.floor(hrs / 24);
    if (days === 1) return 'Ayer';
    if (days < 7) return `Hace ${days}d`;
    return new Date(dateStr).toLocaleDateString('es-ES', { day: '2-digit', month: 'short' });
  } catch {
    return '--';
  }
}

const SOURCE_FALLBACK = { label: 'Desconocido', icon: 'ri-question-line', color: 'text-foreground-400' };
const SEVERITY_FALLBACK = { label: '--', icon: 'ri-question-line', bg: 'bg-background-300', textColor: 'text-foreground-400', badgeBg: 'bg-background-100' };

export default function NotificationCard({
  notification,
  isSelected,
  onToggleSelect,
  onView,
  onMarkRead,
  onToggleImportant,
  onArchive,
  onDelete,
}: NotificationCardProps) {
  const sourceMeta = notificationSourceMeta[notification.source] || SOURCE_FALLBACK;
  const sevMeta = severityMeta[notification.severity] || SEVERITY_FALLBACK;
  const isArchived = notification.archived;

  return (
    <div
      className={`group bg-background-50 rounded-2xl border transition-all ${
        isArchived
          ? 'border-background-200 opacity-60'
          : notification.read
            ? 'border-background-200 hover:border-background-300'
            : 'border-amber-200 bg-amber-50/15 hover:border-amber-300'
      } ${isSelected ? 'ring-2 ring-foreground-950 ring-offset-1' : ''}`}
    >
      <div className="p-3 md:p-4 flex items-start gap-3">
        {/* Checkbox */}
        <button
          onClick={(e) => { e.stopPropagation(); onToggleSelect(notification.id); }}
          className={`w-5 h-5 rounded-md border-2 flex items-center justify-center shrink-0 mt-0.5 transition-colors cursor-pointer ${
            isSelected
              ? 'bg-foreground-950 border-foreground-950'
              : 'border-background-300 bg-background-50 group-hover:border-foreground-400'
          }`}
          aria-label={isSelected ? 'Deseleccionar' : 'Seleccionar'}
        >
          {isSelected && <i className="ri-check-line text-background-50 text-[10px]"></i>}
        </button>

        {/* Severity indicator */}
        <div
          onClick={() => onView(notification)}
          className={`w-2 h-2 rounded-full shrink-0 mt-[7px] ${sevMeta.bg} cursor-pointer`}
          title={sevMeta.label}
        ></div>

        {/* Source icon */}
        <div
          onClick={() => onView(notification)}
          className={`w-9 h-9 rounded-xl flex items-center justify-center shrink-0 cursor-pointer ${
            isArchived || notification.read ? 'bg-background-200' : 'bg-amber-100'
          }`}
        >
          <i className={`${sourceMeta.icon} text-sm ${isArchived || notification.read ? 'text-foreground-400' : sourceMeta.color}`}></i>
        </div>

        {/* Content */}
        <div className="flex-1 min-w-0 cursor-pointer" onClick={() => onView(notification)}>
          <div className="flex items-center gap-2 mb-0.5 flex-wrap">
            <span className="inline-flex px-1.5 py-0.5 rounded-md text-[9px] font-semibold bg-background-100 text-foreground-500">
              {sourceMeta.label}
            </span>
            <span className={`inline-flex px-1.5 py-0.5 rounded-md text-[9px] font-semibold ${sevMeta.badgeBg} ${sevMeta.textColor}`}>
              {sevMeta.label}
            </span>
            {notification.important && (
              <span className="inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded-md text-[9px] font-semibold bg-amber-100 text-amber-700">
                <i className="ri-star-fill text-[8px]"></i>
                Importante
              </span>
            )}
            {!notification.read && !isArchived && (
              <span className="w-1.5 h-1.5 rounded-full bg-amber-500 shrink-0"></span>
            )}
            {isArchived && (
              <span className="inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded-md text-[9px] font-semibold bg-background-100 text-foreground-400">
                <i className="ri-archive-line text-[8px]"></i>
                Archivada
              </span>
            )}
            <span className="text-foreground-400 text-[10px] ml-auto whitespace-nowrap shrink-0">
              {relativeTime(notification.createdAt)}
            </span>
          </div>

          <h3 className={`text-sm font-bold mb-0.5 ${isArchived ? 'text-foreground-500' : notification.read ? 'text-foreground-700' : 'text-foreground-950'}`}>
            {notification.title || 'Sin título'}
          </h3>
          <p className="text-foreground-500 text-xs leading-relaxed line-clamp-2">
            {notification.message || ''}
          </p>

          {/* Quick actions */}
          <div className="flex items-center gap-1.5 mt-2.5 flex-wrap">
            {notification.actionLabel && notification.actionPath && !isArchived && (
              <a
                href={notification.actionPath}
                onClick={(e) => e.stopPropagation()}
                className="inline-flex items-center gap-1 px-2.5 py-1.5 rounded-lg bg-background-100 text-foreground-600 text-[10px] font-semibold hover:bg-background-200 transition-colors cursor-pointer whitespace-nowrap"
              >
                <i className="ri-arrow-right-line text-[10px]"></i>
                {notification.actionLabel}
              </a>
            )}
            {!notification.read && !isArchived && (
              <button
                onClick={(e) => { e.stopPropagation(); onMarkRead(notification); }}
                className="inline-flex items-center gap-1 px-2.5 py-1.5 rounded-lg bg-amber-50 text-amber-600 text-[10px] font-semibold hover:bg-amber-100 transition-colors cursor-pointer whitespace-nowrap"
              >
                <i className="ri-check-line text-[10px]"></i>
                Leída
              </button>
            )}
            <button
              onClick={(e) => { e.stopPropagation(); onToggleImportant(notification); }}
              className={`inline-flex items-center gap-1 px-2.5 py-1.5 rounded-lg text-[10px] font-semibold transition-colors cursor-pointer whitespace-nowrap ${
                notification.important
                  ? 'bg-amber-100 text-amber-700 hover:bg-amber-200'
                  : 'bg-background-100 text-foreground-400 hover:text-amber-600 hover:bg-amber-50'
              }`}
            >
              <i className={`${notification.important ? 'ri-star-fill' : 'ri-star-line'} text-[10px]`}></i>
              {notification.important ? 'Importante' : 'Destacar'}
            </button>
            {!isArchived ? (
              <button
                onClick={(e) => { e.stopPropagation(); onArchive(notification); }}
                className="inline-flex items-center gap-1 px-2.5 py-1.5 rounded-lg bg-background-100 text-foreground-400 hover:text-amber-600 hover:bg-amber-50 text-[10px] font-semibold transition-colors cursor-pointer whitespace-nowrap"
              >
                <i className="ri-archive-line text-[10px]"></i>
                Archivar
              </button>
            ) : (
              <button
                onClick={(e) => { e.stopPropagation(); onArchive(notification); }}
                className="inline-flex items-center gap-1 px-2.5 py-1.5 rounded-lg bg-background-100 text-foreground-400 hover:text-emerald-600 hover:bg-emerald-50 text-[10px] font-semibold transition-colors cursor-pointer whitespace-nowrap"
              >
                <i className="ri-inbox-unarchive-line text-[10px]"></i>
                Restaurar
              </button>
            )}
            <button
              onClick={(e) => { e.stopPropagation(); onDelete(notification); }}
              className="inline-flex items-center gap-1 px-2.5 py-1.5 rounded-lg bg-background-100 text-foreground-400 hover:text-red-500 hover:bg-red-50 text-[10px] font-semibold transition-colors cursor-pointer whitespace-nowrap"
            >
              <i className="ri-delete-bin-line text-[10px]"></i>
              Eliminar
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}